import FIELDTYPES, { FIELDS } from './fieldtypes'
import Ranger from './ranger'

export const VERSION = '0.9.23'
export const FIELDSTYLES = [
  { value: 'regular', label: 'Regular' },
  { value: 'filled', label: 'Filled' },
  { value: 'outlined', label: 'Outlined' },
  { value: 'solo', label: 'Solo' }
]

//
// Constants
//
export const CONST = {
  NAMESPACE: 'vgf',
  CLASS_FIELD: 'vgf-f',
  DEBOUNCE_UPDATE: 500,
  DEBOUNCE_QUEUE: 300,
  DEBOUNCE_SAVE: 1000,
  TEXTAREA_ROWS: 3,
  SIZE_MIN: 4,
  SIZE_MAX: 12,
  SIZE_DEFAULT: 12,
  SIZE_STACKED: 4,
  FIELD_STYLES: FIELDSTYLES.map((fs) => fs.value),
  VALID_FIELDS: [
    'autocomplete',
    'button',
    'checkbox',
    'combobox',
    'select',
    'date',
    'daterange',
    'time',
    'infotext',
    'email',
    'number',
    'text',
    'textarea',
    'phone',
    'password',
    'switch',
    'radio',
    'slider',
    'range',
    'rating',
    'sparkline',
    'spacer',
    'divider',
    'subheader',
    'duration',
    'link',
    'gap',
    'ratingbutton'
  ],
  NOEDIT_FIELDS: ['rating', 'gap'], // hide edit action in v-gridform-field-toolbar.vue
  GFB_NAME: '_vgfb',
  GFBEDIT_NAME: '_vgfbe',
  DEFAULT_TREE_OPEN: ['grouptext', 'groupselect', 'grouprating'],
  DEFAULT_GFB_SETTINGS: {
    autoEditMode: false
  },
  EXPAND_ICON: 'mdi-plus-box-outline',
  COLLAPSE_ICON: 'mdi-minus-box-outline',
  EXPAND_ICON_ALL: 'mdi-expand-all-outline',
  COLLAPSE_ICON_ALL: 'mdi-collapse-all-outline'
}

export const REGEX_IS_TEXT = /^(text|textarea|date|password|number|phone|email|autocomplete|combobox|select|time|daterange|duration|link)$/i
// see https://emailregex.com/
export const REGEX_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

//
// Default callbacks
//
export const DEFAULT_CALLBACKS = {
  onSubmitAction: () => {},
  onCancelAction: () => {},
  onDrawerAction: () => {},
  beforeValidation: () => {},
  onValidAction: () => {},
  onFieldChange: () => {},
  onFieldSelect: () => {},
  onFieldClick: () => {},
  onFieldHover: () => {},
  onFieldListChange: () => {},
  onButtonClick: () => {},
  onHoverChange: () => {},
  onFieldSelection: () => {},
  onFabClick: () => {},
  onFabAction: () => {},
  onFieldDelete: () => {},
  onBlockCreate: () => {},
  onBlockReset: () => {},
  onBlockRemove: () => {},
  onFieldsetToggle: () => {},
  onAppendClick: () => {},
  onRowsChange: () => {},
  onPrependInnerClick: () => {}
}
export const DEFAULT_CALLBACKS_BUILDER = {
  onSelectType: () => {},
  onFieldSelection: () => {},
  onRowsChange: () => {},
  onBlockCreate: () => {},
  onBlockUpdate: () => {}
}

//
// Form constants
//
export const GLOBAL_OPTIONS = {
  fieldstyle: 'regular',
  dark: false,
  size: CONST.SIZE_DEFAULT,
  stacked: false,
  elevation: 4,
  flat: true,
  store: false,
  center: true,
  disabled: false,
  dense: false, // true=activate dense mode for controls
  validate: true, // use to deactivate internal validation and auto submit button state
  lazyValidation: true, // validate fields after input, set to false to validate immediatly
  verboseValidation: false,
  icon: 'prependInnerIcon',
  padding: 'pa-0',
  fillHeight: false,
  fluid: false,
  editable: false,
  mousetrap: false,
  submitOnEnter: false,
  layoutClass: '',
  contentClass: '',
  fieldlistClass: '',
  autoFooter: false,
  loading: false, // set loading property of submit button
  destroy: false, // destroy store when gridform component is destroyed
  keys: false, // bind F4 for submit and F10 for cancel
  rating: null, // TODO current solution for Scorecards {large}
  color: '', // formular background color
  fieldlistFocusable: false, // activate visible marker for focused fieldlist
  fieldlistMoveable: false, // append button bar to move fieldlist
  formatter: { // format and input masks
    // dateFormat: 'DD.MM.YYYY',
    // datePlaceholder: 'TT.MM.JJJJ',
    // datePattern: ['d', 'm', 'Y'],
    // delimiter: '.'
    dateFormat: 'YYYY-MM-DD',
    // datePlaceholder: 'YYYY-MM-DD',
    datePattern: ['Y', 'm', 'd'],
    delimiter: '-'
  }
}

export const DRAWER_OPTIONS = {
  drawerTitle: '',
  drawerItems: []
}

export const TOOLBAR_OPTIONS = {
  toolbar: false,
  toolbarTitle: '',
  toolbarDrawer: false,
  toolbarMenu: false,
  toolbarColor: '',
  toolbarAbsolute: false
}

export const FORM_PROPERTIES = {
  header: '',
  i18n: '',
  actions: false,
  actiontext: 'OK',
  actionOptions: null,
  hover: false,
  rows: [],
  ...GLOBAL_OPTIONS,
  ...DRAWER_OPTIONS,
  ...TOOLBAR_OPTIONS
}

// gridform rows format
export const VGF_FORM_OPTIONS = [
  [
    { name: 'name', label: 'Name', size: 4, hint: 'unique gridform name' },
    { name: 'header', label: 'Header', size: 8, hint: 'form header' }
  ],
  [
    { name: 'toolbar', hint: 'toolbar?', type: 'switch', size: 2 },
    {
      name: 'toolbarTitle',
      label: 'Titel',
      hint: 'toolbar title',
      watch: 'toolbar'
    },
    {
      name: 'toolbarDrawer',
      hint: 'drawer?',
      type: 'switch',
      size: 2,
      watch: 'toolbar'
    },
    {
      name: 'toolbarMenu',
      hint: 'menu?',
      type: 'switch',
      size: 2,
      watch: 'toolbar'
    }
  ],
  [
    {
      name: 'size',
      label: 'Formular Size',
      type: 'slider',
      min: 4,
      max: 12,
      hideDetails: true
    }
  ],
  [
    { name: 'flat', hint: 'flat?', type: 'switch', size: 2 },
    {
      name: 'elevation',
      label: 'Elevation',
      type: 'slider',
      size: 10,
      min: 0,
      max: 20,
      step: 2,
      watch: '!flat',
      hideDetails: true
    }
  ],
  [
    { name: 'dark', type: 'switch', hint: 'dark theme?' },
    { name: 'stacked', type: 'switch', hint: 'stacked fields?' },
    { name: 'store', type: 'switch', hint: 'store field data?' },
    { name: 'center', type: 'switch', hint: 'center form?' },
    { name: 'lazyValidation', type: 'switch', hint: 'lazy validation?' }
  ],
  [
    {
      name: 'fieldstyle',
      label: 'Fieldstyle',
      type: 'radio',
      items: FIELDSTYLES
    }
  ],
  [{ name: 'fillHeight', type: 'switch', hint: 'fill height?' }]
]

export const VGF_FORM_OPTIONS_DEFAULTS = (options) => {
  return VGF_FORM_OPTIONS.map((row) => {
    return row.map((col) => {
      const option = options[col.name]
      col.value =
        option && typeof option.value !== 'undefined'
          ? option.value
          : FORM_PROPERTIES[col.name]
      return col
    })
  })
}

// Config properties that must be copied to field properties
export const CONFIG_TO_FIELDS = ['fieldstyle', 'disabled', 'dense']

//
// Field constants
//
export const DEFAULT_FLAGS = {
  disabled: false,
  forceDisabled: false, // prevents enabling by global config option or adapter call
  forceEnabled: false, // prevents disabling by global config option or adapter call
  readonly: false,
  required: false,
  autofocus: false,
  spellcheck: false,
  clearable: true,
  hideDetails: true,
  hidden: false,
  exclude: false, // as hidden but skip validation
  speech: false,
  browserAutocomplete: 'off',
  hovered: false,
  store: true,
  dense: false,
  validateOnBlur: true,
  dirty: false // field validation only when dirty is true
}

export const FIELD_PROPERTIES = {
  name: '',
  type: 'text',
  value: null,
  label: '',
  placeholder: '',
  hint: '',
  messages: null,
  fieldstyle: 'regular',
  size: 12,
  inputType: null,
  cls: null,
  padClass: 'pa-2', // to replace default "pa-2" class for fields
  prependIcon: false,
  prependInnerIcon: false,
  appendIcon: null,
  appendOuterIcon: false,
  ...DEFAULT_FLAGS,
  rules: null,
  rowinfo: null,
  editMode: false,
  fieldlist: null,
  rating: null,
  ratingScroll: null, // adds additional button to scroll rating up and down
  // type depending props
  time: false,
  min: 0,
  max: 10,
  items: null,
  rows: CONST.TEXTAREA_ROWS,
  autoGrow: true, // for type: "textarea" - grow/shrink field depending on content
  tickLabels: null, // calculated tickLabels from field properties min, max and step
  customLabels: null, // overwrite calculated tickLabels
  multiple: false, // combobox or radio selection
  toggle: null, // list of fields for type="divider" to support toggle hidden state
  collapsed: false, // initial state for the toggle
  export: false, // include the field in export
  weighting: 100, // percentage to weight scores
  row: true, // show options horizontal
  step: 1 // step size for sliders
}

export const FIELD_PROPERTIES_KEYS = Object.keys(FIELD_PROPERTIES)

// TODO translate error messages
export const RULES = {
  termRules: [(v) => typeof v !== 'undefined' && !!`${v}` || '*Pflichtfeld'],
  nameRules: [
    (v) => !!v || 'Name is required',
    (v) => (v && v.length <= 10) || 'Name must be less than 10 characters'
  ],
  emailRules: [
    (v) => !v || REGEX_EMAIL.test(v) || 'Bitte eine gültige E-Mail eingeben.'
  ],
  dateRules: [
    (v) => !v || !!Ranger.parseDate(v, true) || 'Ungültiges Datumsformat.'
  ],
  timeRules: [
    (v) => !v || !!Ranger.parseTime(v) || 'Ungültiges Zeitformat.'
  ],
  rangeRules: [
    (v) => {
      console.log('rangeRule v', v)
      return 'RANGE'
    }
  ],
  checkboxRequired: [(v) => !!v || 'Auswahl muss bestätigt werden!'],
  minLengthRules: [
    (v, data, field) => {
      if (!field) {
        console.warn('invalid rule call')
        return false
      }
      if (!Number.isInteger(field.minlength)) {
        console.warn('invalid minlength value')
        return false
      }
      return v && v.length >= field.minlength || `Eingabe muß mindestens ${field.minlength} Zeichen lang sein`
    }
  ],
  maxLengthRules: [
    (v, field) => v && v.length < field.maxlength || `Eingabe darf höchstens ${field.maxlength} Zeichen lang sein`
  ]
}

export const DEFAULT_DATE = {
  format_de: 'DD.MM.YYYY',
  hint_de: 'JJJJ-MM-TT',
  format_en: 'YYYY-MM-DD',
  hint_en: 'YYYY-MM-DD',
  format: 'YYYY-MM-DD',
  hint: 'JJJJ-MM-TT',
  icon: FIELDS.date.icon
}

export const VGF_FIELDS = {
  header: 'Gridform Field Selection',
  size: 12,
  flat: true,
  hover: true,
  padding: 'pa-0',
  rows: [
    [
      {
        name: 'text',
        label: 'Text',
        disabled: true,
        hint: 'Singleline text field'
      }
    ],
    [
      {
        name: 'textarea',
        label: 'Textarea',
        disabled: true,
        hint: 'Multiline text field'
      }
    ],
    [
      {
        name: 'checkbox',
        label: 'Checkbox',
        type: 'checkbox',
        size: 6,
        disabled: true,
        hint: 'Checkbox'
      },
      {
        name: 'switch',
        label: 'Switch',
        type: 'switch',
        size: 6,
        disabled: true,
        hint: 'Switch'
      }
    ],
    [
      {
        name: 'radio',
        label: 'Radiogroup',
        size: 11,
        disabled: true,
        hint: 'Radiogroup'
      }
    ],
    [
      {
        name: 'slider',
        label: 'Slider',
        type: 'slider',
        size: 11,
        disabled: true,
        hint: 'Slider',
        min: 0,
        max: 24,
        step: 2,
        value: 12
      }
    ],
    [
      {
        name: 'rangeslider',
        label: 'Range Slider',
        type: 'range',
        size: 11,
        disabled: true,
        hint: 'Range Slider',
        min: 0,
        max: 24,
        step: 2,
        value: [8, 17]
      }
    ]
  ]
}

export const VGF_FIELDS_PROPS = (type, defaults) => {
  const rows = FIELDTYPES[type] ? FIELDTYPES[type](defaults) : FIELDTYPES.text(defaults)
  const props = {
    header: 'Type',
    size: 12,
    center: false,
    toolbar: true,
    toolbarTitle: 'Gridform Field Properties',
    rows
  }

  props.header = 'Type: ' + type

  return props
}

export const HOVER_BUTTONS_GLOBAL = [
  { icon: 'edit', hint: 'Bearbeiten' },
  { icon: 'build', hint: 'Erzeugen' },
  { icon: 'cancel', hint: 'Beenden' }
]
export const HOVER_BUTTONS = {
  text: [
    { icon: 'event', hint: 'Datum' },
    { icon: 'time', hint: 'Zeit' },
    { icon: 'lock', hint: 'Passwort' },
    { icon: 'email', hint: 'E-Mail' },
    { icon: 'phone', hint: 'Telefon' },
    { icon: 'euro_symbol', hint: 'Preis' }
  ]
}

export const VGFB_TOUR_ROWS = [
  [
    {
      name: 'vgf-f1',
      label: 'Anwahlversuche',
      hint: 'Ist die Anzahl der Anwahlversuche angemessen, bis MA erreicht wird?',
      min: 0,
      max: 3,
      tickLabels: [
        '0',
        '1',
        '2',
        '3'
      ],
      type: 'slider',
      value: 0,
      fieldlist: {
        id: 'vgfb-tour-rows',
        initials: 'ERREICHBAR',
        name: 'Erreichbarkeit',
        description: 'Anwahlversuche, Wartezeit, Weiterleitung, Hinweise',
        collapsed: false
      },
      rating: {
        min: 0,
        max: 3,
        score: 0
      }
    }
  ]
]
